import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Upload, AlertTriangle, CheckCircle, DollarSign } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BookerImportPage() {
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importId, setImportId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      const res = await fetch(`${API}/api/admin/payroll-periods`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        const openPeriods = data.filter(p => p.status !== 'finalized' && p.status !== 'locked');
        setPeriods(openPeriods);
      }
    } catch (err) {
      setError('Failed to load periods');
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setPreview(null);
    setImportId(null);
    setError('');
    setSuccess('');
  };

  const handleUpload = async () => {
    if (!file || !selectedPeriod) {
      setError('Please select a period and file');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('period_id', selectedPeriod);

    try {
      const res = await fetch(`${API}/api/admin/import/booker/preview`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      const data = await res.json();

      if (res.ok) {
        setPreview(data.preview);
        setImportId(data.import_id);
      } else {
        setError(data.detail || 'Preview failed');
      }
    } catch (err) {
      setError('Upload failed');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const res = await fetch(`${API}/api/admin/import/booker/confirm/${importId}`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(`Import complete: ${data.rows_written} rows written, ${data.rows_skipped} skipped`);
        setPreview(null);
        setImportId(null);
        setFile(null);
      } else {
        setError(data.detail || 'Import failed');
      }
    } catch (err) {
      setError('Import failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-serif font-bold text-stone-900">Booker Import</h1>
          <p className="text-stone-600 mt-2">Upload Staff Sales Detailed export to import service sales, retail, and tips</p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="border-green-200 bg-green-50">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{success}</AlertDescription>
          </Alert>
        )}

        {!preview && (
          <Card>
            <CardHeader>
              <CardTitle>Upload Sales Export</CardTitle>
              <CardDescription>Select target period and Booker Excel file</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Target Period</label>
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  className="w-full border border-stone-300 rounded-md px-3 py-2"
                >
                  <option value="">Select period</option>
                  {periods.map(p => (
                    <option key={p._id} value={p._id}>
                      {p.start_date} to {p.end_date} ({p.status})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Booker Excel File</label>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  className="w-full"
                />
              </div>

              <Button onClick={handleUpload} disabled={loading || !file || !selectedPeriod}>
                <Upload className="mr-2 h-4 w-4" />
                {loading ? 'Uploading...' : 'Upload & Preview'}
              </Button>
            </CardContent>
          </Card>
        )}

        {preview && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Import Preview</CardTitle>
                <CardDescription>Review before confirming</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="font-medium text-stone-700">Period</div>
                    <div>{preview.period_start} to {preview.period_end}</div>
                  </div>
                  <div>
                    <div className="font-medium text-stone-700">Parsed Sales</div>
                    <div>{preview.parsed_count}</div>
                  </div>
                  <div>
                    <div className="font-medium text-stone-700">Matched Employees</div>
                    <div>{preview.employees_matched.length}</div>
                  </div>
                  <div>
                    <div className="font-medium text-stone-700">Unassigned</div>
                    <div>{preview.unassigned_count}</div>
                  </div>
                </div>

                {preview.unmatched_names && preview.unmatched_names.length > 0 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <div className="font-semibold mb-2">Unmatched Employees</div>
                      <div className="text-sm">{preview.unmatched_names.join(', ')}</div>
                      <div className="mt-2 text-sm">These sales will be skipped.</div>
                    </AlertDescription>
                  </Alert>
                )}

                <div className="flex gap-3">
                  <Button onClick={handleConfirm} disabled={loading}>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Confirm Import
                  </Button>
                  <Button variant="outline" onClick={() => { setPreview(null); setImportId(null); }}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Matched Employees</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {preview.employees_matched.map((emp) => (
                    <div key={emp.employee_id || emp.username} className="flex justify-between py-2 border-b border-stone-200 last:border-0">
                      <span>{emp.source_name}</span>
                      <span className="text-stone-600">{emp.canonical_name} (@{emp.username})</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
